import React, { useEffect, useState } from 'react';
import * as Select from '@radix-ui/react-select';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { inputCls } from '../primitives/inputClasses';
import { controlAnchorProps } from 'modules/Common/utils/gapAnchor';

// Radix Select for short option lists (≤ ~30 items). For long/searchable
// lists use SearchSelectField.
//
// `options`: [{ value, label }] | [{ value, label, disabled }]
// `valueKey` / `labelKey`: customize when options have a different shape.
// `ariaLabel`: the trigger's accessible name when it is NOT the placeholder —
// a compact select that takes its meaning from a neighbour (a unit next to the
// number it qualifies) needs to say what it is without a visible label of its
// own, and its placeholder is the wrong sentence for that.
//
// An option may carry an empty value — "No specific installation", "— Direction —".
// Radix throws on `<Select.Item value="">` (the empty string is its "cleared"
// value), which unmounts the whole page, so such an option crosses Radix under
// a sentinel and is unwrapped on the way out. The field still stores ''.
const EMPTY_OPTION = '__empty__';

export default function SelectField({
  field,
  fieldState,
  id,
  options = [],
  valueKey = 'value',
  labelKey = 'label',
  placeholder,
  ariaLabel,
  disabled,
  className = '',
  anchor,
  empty,
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape, true);
    return () => window.removeEventListener('keydown', closeOnEscape, true);
  }, [open]);
  const resolvedPlaceholder = placeholder ?? t('common:fields.selectPlaceholder', 'Select...');
  const items = options.map((o) => ({
    value: String(o[valueKey] ?? '') || EMPTY_OPTION,
    label: o[labelKey] ?? o.name ?? String(o[valueKey] ?? ''),
    disabled: o.disabled,
  }));
  const hasEmptyOption = items.some((it) => it.value === EMPTY_OPTION);

  const triggerClass = inputCls(
    !!fieldState?.error,
    `inline-flex items-center justify-between ${className}`,
  );

  // Pass `undefined` (not `''`) when the field has no value yet so Radix
  // treats the trigger as truly unset. With `''` Radix locks onto the
  // empty string and the trigger can stick on the placeholder when the
  // real value lands one render later (uncontrolled→controlled bug). When an
  // option stands for the empty value, the empty value selects that option.
  const isEmpty = field.value == null || field.value === '';
  const radixValue = isEmpty ? (hasEmptyOption ? EMPTY_OPTION : undefined) : String(field.value);

  // Render the matching item's label directly in the trigger instead of
  // relying on <Select.Value>. Radix's Select.Value reads its display
  // text from the matching <Select.Item>'s ItemText, but the Items only
  // mount when Content is open — so until the user opens the dropdown
  // the trigger shows the placeholder even when a valid value is set.
  const selectedItem = radixValue ? items.find((it) => it.value === radixValue) : undefined;

  return (
    <Select.Root
      open={open}
      onOpenChange={setOpen}
      value={radixValue}
      onValueChange={(v) => field.onChange(v === EMPTY_OPTION ? '' : v)}
      disabled={disabled}
    >
      <Select.Trigger
        id={id}
        className={triggerClass}
        aria-label={ariaLabel ?? resolvedPlaceholder}
        {...controlAnchorProps(anchor, empty ?? !field?.value)}
      >
        {selectedItem ? (
          <span className="truncate text-left">{selectedItem.label}</span>
        ) : (
          <span className="truncate text-left text-slate-400">{resolvedPlaceholder}</span>
        )}
        <Select.Icon>
          <ChevronDownIcon />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={4}
          data-cg-escape-closes=""
          style={{
            width: 'var(--radix-select-trigger-width)',
            minWidth: 'var(--radix-select-trigger-width)',
            zIndex: 99998,
          }}
          className="bg-white rounded-xl shadow-lg border border-slate-200 max-h-[300px] overflow-hidden"
        >
          <Select.ScrollUpButton className="flex items-center justify-center h-6 bg-white">
            <ChevronUpIcon />
          </Select.ScrollUpButton>
          <Select.Viewport className="p-1">
            {items.map((it) => (
              <Select.Item
                key={it.value}
                value={it.value}
                disabled={it.disabled}
                className="relative flex items-center px-3 py-2 text-sm rounded-md cursor-pointer select-none data-[highlighted]:bg-primaryColor/10 data-[highlighted]:outline-none data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed"
              >
                <Select.ItemText>{it.label}</Select.ItemText>
                <Select.ItemIndicator className="ml-auto pl-3">
                  <CheckIcon />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
          <Select.ScrollDownButton className="flex items-center justify-center h-6 bg-white">
            <ChevronDownIcon />
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
